import { useState } from "react";
import { Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable password dialog.
 *
 * mode="self"  → calls POST /api/auth/change-password (requires current + new)
 * mode="admin" → calls POST /api/admin/users/{userId}/reset-password (requires only new)
 */
export const ChangePasswordDialog = ({ mode = "self", userId, targetLabel, trigger }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    if (mode === "self" && !current) return toast.error("Enter your current password");

    setLoading(true);
    try {
      if (mode === "self") {
        await api.post("/auth/change-password", { current_password: current, new_password: next });
        toast.success("Password updated. Please remember it — you'll need it next time you log in.");
      } else {
        await api.post(`/admin/users/${userId}/reset-password`, { new_password: next });
        toast.success(`Password reset for ${targetLabel || "user"}. Any active sessions have been signed out.`);
      }
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid={mode === "self" ? "change-password-dialog" : "admin-reset-password-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-brand-navy flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-brand-red" />
            {mode === "self" ? "Change your password" : `Reset password`}
          </DialogTitle>
          <DialogDescription>
            {mode === "self"
              ? "You'll need the new password next time you log in."
              : <>Setting a new password for <span className="font-semibold text-brand-navy">{targetLabel}</span>. Any active sessions will be signed out immediately.</>}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 py-2">
          {mode === "self" && (
            <div>
              <Label className="text-brand-navy font-semibold">Current password</Label>
              <div className="mt-2 relative">
                <Input
                  type={showCurrent ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  placeholder="Current password"
                  className="h-11 pr-12 rounded-xl border-brand-navy/15"
                  data-testid="cp-current-input"
                  autoFocus
                  required
                />
                <button type="button" onClick={() => setShowCurrent((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/50 hover:text-brand-red" data-testid="cp-current-toggle" aria-label="toggle visibility">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
          <div>
            <Label className="text-brand-navy font-semibold">New password</Label>
            <div className="mt-2 relative">
              <Input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="Min 8 characters"
                className="h-11 pr-12 rounded-xl border-brand-navy/15"
                data-testid="cp-new-input"
                required
                minLength={8}
              />
              <button type="button" onClick={() => setShowNext((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/50 hover:text-brand-red" data-testid="cp-new-toggle" aria-label="toggle visibility">
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-brand-navy font-semibold">Confirm new password</Label>
            <Input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter new password"
              className={`mt-2 h-11 rounded-xl border-brand-navy/15 ${confirm && confirm !== next ? "border-red-500" : ""}`}
              data-testid="cp-confirm-input"
              required
            />
            {confirm && confirm !== next && <p className="mt-1.5 text-xs text-red-600 font-semibold">Passwords do not match</p>}
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              type="submit"
              disabled={loading || !next || next !== confirm || (mode === "self" && !current)}
              data-testid="cp-submit-btn"
              className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "self" ? "Update password" : "Reset password")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
